import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Undo2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, MODE_LABEL } from "@/lib/api";
import { Badge } from "@/pages/Account";
import { SCHEMAS } from "./schemas";

const Sel = ({ value, onChange, options, testid }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
    {options.map((o) => <option key={o} value={o}>{MODE_LABEL[o] || o || "—"}</option>)}
  </select>
);

function PlansEditor({ value = [], onChange }) {
  const upd = (i, patch) => onChange(value.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const price = (p, c) => p.prices?.[c] ?? "";
  const setPrice = (i, c, v) => upd(i, { prices: { ...(value[i].prices || {}), [c]: v === "" ? undefined : Number(v) } });
  return (
    <div className="space-y-3" data-testid="plans-editor">
      {value.map((p, i) => (
        <div key={p.id || i} className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-6" data-testid={`plan-row-${i}`}>
          <Input className="sm:col-span-2" placeholder="Nombre del plan" value={p.name || ""} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`plan-name-${i}`} />
          <div className="sm:col-span-2"><Sel value={p.mode} onChange={(v) => upd(i, { mode: v })} options={Object.keys(MODE_LABEL)} testid={`plan-mode-${i}`} /></div>
          <Input type="number" placeholder="USD" value={price(p, "USD")} onChange={(e) => setPrice(i, "USD", e.target.value)} data-testid={`plan-usd-${i}`} />
          <Input type="number" placeholder="CRC" value={price(p, "CRC")} onChange={(e) => setPrice(i, "CRC", e.target.value)} data-testid={`plan-crc-${i}`} />
          <Input className="sm:col-span-4" placeholder="Características (separadas por ;)" value={(p.features || []).join("; ")}
            onChange={(e) => upd(i, { features: e.target.value.split(";").map((x) => x.trim()).filter(Boolean) })} />
          <Input type="number" placeholder="Días de prueba" value={p.trial_days ?? ""} onChange={(e) => upd(i, { trial_days: e.target.value ? Number(e.target.value) : undefined })} />
          <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="text-sm text-red-600">Eliminar</button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { name: "", mode: "monthly", prices: {} }])} className="text-sm font-semibold text-brand" data-testid="plan-add">+ Agregar plan</button>
      <p className="text-xs text-slate-500">Precios definidos por mercado y moneda (sin conversión automática). Deje vacío si el precio aún no está aprobado.</p>
    </div>
  );
}

function FieldInput({ fd, value, onChange, categories }) {
  const id = `field-${fd.k}`;
  switch (fd.type) {
    case "textarea": return <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
    case "number": return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={id} />;
    case "bool": return <Switch checked={!!value} onCheckedChange={onChange} data-testid={id} />;
    case "select": return <Sel value={value} onChange={onChange} options={["", ...fd.options.filter(Boolean)]} testid={id} />;
    case "category": return <Sel value={value} onChange={onChange} options={["", ...categories]} testid={id} />;
    case "list": return <Textarea rows={3} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={id} />;
    case "date": return <Input type="date" value={(value || "").slice(0, 10)} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
    case "password": return <Input type="password" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} autoComplete="new-password" />;
    case "json": return <Textarea rows={3} className="font-mono text-xs" value={typeof value === "string" ? value : JSON.stringify(value || [], null, 1)} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
    case "plans": return <PlansEditor value={value || []} onChange={onChange} />;
    default: return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  }
}

const cell = (v) => {
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString("es-CR");
  if (["active", "paid", "draft", "published", "approved", "pending", "nuevo", "refunded", "pending_payment", "payment_failed", "revoked", "cancelled", "trialing", "pendiente_confirmacion", "initiated", "failed"].includes(v)) return <Badge s={v} />;
  return v === null || v === undefined || v === "" ? "—" : String(v);
};

function EditDialog({ res, schema, item, onClose, onSaved, categories }) {
  const [form, setForm] = useState(item || {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const body = { ...form };
    for (const fd of schema.fields) {
      if (fd.type === "list") body[fd.k] = (body[fd.k] || []).map((x) => x.trim()).filter(Boolean);
      if (fd.type === "json" && typeof body[fd.k] === "string") {
        try { body[fd.k] = JSON.parse(body[fd.k] || "[]"); } catch { return toast.error(`JSON inválido en ${fd.label}`); }
      }
    }
    if (item?.id) { const keep = schema.fields.map((x) => x.k); Object.keys(body).forEach((k) => !keep.includes(k) && delete body[k]); }
    setBusy(true);
    try {
      if (item?.id) await api.put(`/admin/${res}/${item.id}`, body); else await api.post(`/admin/${res}`, body);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="resource-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Editar" : "Nuevo"} · {schema.title}</DialogTitle></DialogHeader>
        {schema.fields ? (
          <div className="grid gap-4">
            {schema.fields.map((fd) => (
              <label key={fd.k} className="grid gap-1.5"><span className="text-sm font-medium text-slate-700">{fd.label}</span>
                <FieldInput fd={fd} value={form[fd.k]} onChange={(v) => setForm((s) => ({ ...s, [fd.k]: v }))} categories={categories} /></label>
            ))}
            <button disabled={busy} onClick={save} className="btn-cc w-fit" data-testid="resource-save">{busy ? "Guardando…" : "Guardar"}</button>
          </div>
        ) : null}
        {item?.id && <pre className="mt-4 max-h-80 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-200" data-testid="resource-json">{JSON.stringify(item, null, 2)}</pre>}
      </DialogContent>
    </Dialog>
  );
}

export default function ResourceManager({ user }) {
  const { res } = useParams();
  const schema = SCHEMAS[res];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [cats, setCats] = useState([]);
  const load = useCallback(() => { setRows(null); api.get(`/admin/${res}`).then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); }); }, [res]);
  useEffect(() => { load(); if (res === "products") api.get("/public/categories").then((r) => setCats(r.data.map((c) => c.name))); }, [load, res]);
  if (!schema) return <p>Módulo no encontrado</p>;
  const del = async (row) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/admin/${res}/${row.id}`); toast.success("Eliminado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const refund = async (row) => {
    if (!window.confirm(`¿Reembolsar el pedido ${row.number}? Se revocarán sus licencias.`)) return;
    try { await api.post(`/admin/orders/${row.id}/refund`); toast.success("Reembolso registrado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = (rows || []).filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid={`admin-resource-${res}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{schema.title}</h1>
        <div className="flex gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 pl-8" data-testid="resource-search" /></div>
          {!schema.readonly && !schema.noCreate && <button onClick={() => setEdit({})} className="btn-cc !py-2" data-testid="resource-new"><Plus className="h-4 w-4" /> Nuevo</button>}
        </div>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{schema.cols.map((c) => <th key={c} className="p-3 font-medium">{c}</th>)}<th className="p-3" /></tr></thead>
          <tbody>
            {rows === null ? <tr><td className="p-6 text-slate-400" colSpan={9}>Cargando…</td></tr> : !shown.length ? <tr><td className="p-6 text-slate-400" colSpan={9} data-testid="resource-empty">Sin registros.</td></tr>
              : shown.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`resource-row-${r.id}`}>
                  {schema.cols.map((c) => <td key={c} className="max-w-[260px] truncate p-3">{cell(r[c])}</td>)}
                  <td className="whitespace-nowrap p-3 text-right">
                    <button onClick={() => setEdit(r)} className="rounded p-1.5 hover:bg-slate-100" aria-label="Editar" data-testid={`resource-edit-${r.id}`}>{schema.readonly ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}</button>
                    {res === "orders" && r.status === "paid" && user.role === "admin" && <button onClick={() => refund(r)} className="rounded p-1.5 text-amber-700 hover:bg-amber-50" aria-label="Reembolsar" data-testid={`order-refund-${r.id}`}><Undo2 className="h-4 w-4" /></button>}
                    {!schema.readonly && <button onClick={() => del(r)} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Eliminar" data-testid={`resource-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {edit && <EditDialog res={res} schema={schema} item={edit.id ? edit : null} categories={cats} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
